import React, { Component } from "react";
import PropTypes from "prop-types";
import { Transition } from "react-transition-group";

import { MdClose } from "react-icons/md";

import { SaveButton, ResetButton } from "../Button";

import "../../styles/components/modals/_modal.scss";

const duration = 300;

const defaultStyle = {
  transition: `opacity ${duration}ms ease-in-out`,
  opacity: 0,
  pointerEvents: "none",
};

const transitionStyles = {
  entering: { opacity: 0 },
  entered: { opacity: 1, pointerEvents: "auto" },
};

const Fade = ({ in: inProp, children }) => (
  <Transition in={inProp} timeout={duration}>
    {(state) => (
      <div
        style={{
          ...defaultStyle,
          ...transitionStyles[state],
        }}
      >
        {children}
      </div>
    )}
  </Transition>
);

/**
 * A generic modal for use across the app.
 *
 * Usage example;
 *
 * 	<Modal isOpen={this.state.isModalOpen} modalName="generic-modal" onClose={() => this.setState({isModalOpen:false})}>
 *    <p>This is The modal content!</p>
 *  </Modal>
 *
 * @type {Object}
 */
class Modal extends Component {
  static propTypes = {
    /**
     * Is the modal open or closed??
     */
    isOpen: PropTypes.bool.isRequired,

    /**
     * The name of the modal. This is added to modal classes.
     */
    modalName: PropTypes.string.isRequired,

    /**
     * Method to run when the modal closes. This will be run after
     * the close animation has completed.
     */
    onClose: PropTypes.func,

    /**
     * Method to run when the modal should close. This can prevent
     * close of the modal if you return false.
     */
    onBeforeClose: PropTypes.func,

    /**
     * should this modal have a close button
     */
    hasCloseButton: PropTypes.bool,

    /**
     * Should be a node, or an array of nodes.
     * This is the modal content.
     */
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  };

  static defaultProps = {
    /**
     * Noop for a default onClose call.
     */
    onClose: () => {},

    /**
     * Default onBeforeClose — always allow close.
     */
    onBeforeClose: () => true,

    /**
     * by default we allow closing of the modal
     */
    hasCloseButton: true,
  };

  /**
   * Define the initial state of the component.
   *
   * @author Sam Sehnert <sam@customd.com>
   *
   * @param  {object}  props      The props passed to this component.
   * @return {void}
   */
  constructor(props) {
    super(props);

    const { isOpen } = props;
    this.state = { isOpen };
  }

  /**
   * Update the components isOpen state based on the nextProps.
   *
   * @author Sam Sehnert <sam@customd.com>
   *
   * @param  {object}  nextProps  The new props the component is recieving.
   * @return {void}
   */
  componentWillReceiveProps(nextProps) {
    const { isOpen } = nextProps;

    if (isOpen !== this.state.isOpen) {
      this.setState({ isOpen });
    }
  }

  /**
   * Should this props/state update re-render the component?
   * We make this distinction based on whether the the isOpen state
   * changes, OR if the nextProps say's the modal is open.
   *
   * @author Sam Sehnert <sam@customd.com>
   *
   * @param  {object}  nextProps  The new props the component is recieving.
   * @param  {object}  nextState  The new state the component is recieving.
   * @return {bool}               Whether the component should update or not.
   */
  shouldComponentUpdate(nextProps, nextState) {
    return nextProps.isOpen || nextState.isOpen !== this.state.isOpen;
  }

  /**
   * Prevents clicks to the modal content from closing the modal.
   *
   * @author Sam Sehnert <sam@customd.com>
   *
   * @param  {object}  e          A JavaScript event object.
   * @return {void}
   */
  preventAndStopBubbling(e) {
    //if it is an a tag, we want the bubble to continue
    if (e.target.nodeName !== "A") {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  /**
   * Close the modal. We delay the onClose
   * event until after the animation has played.
   *
   * @author Sam Sehnert <sam@customd.com>
   * @since  [version] Introduced
   * @return {[type]}  [description]
   */
  onClose() {
    const { onBeforeClose, onClose } = this.props;

    if (onBeforeClose() !== false) {
      this.setState({ isOpen: false });

      setTimeout(onClose, duration);
    }
  }

  /**
   * Render the modal!
   */
  render() {
    const { modalName, children, hasCloseButton, onClose } = this.props;

    return (
      <Fade key={modalName} in={this.state.isOpen}>
        <div className={"modal modal--" + modalName}>
          {hasCloseButton && (
            <div className="modal__close">
              <button onClick={typeof onClose === 'function' ? onClose : this.onClose}>
                <MdClose size={21} />
              </button>
            </div>
          )}
          <div className={"modal__wrap " + modalName} onClick={this.preventAndStopBubbling}>
            {children}
          </div>
        </div>
      </Fade>
    );
  }
}

export class ModalButtonGroup extends Component {
  render() {
    return <div className="modal__submit-buttons">{this.props.children}</div>;
  }
}

export class ModalCancelButton extends ResetButton {
  static propTypes = {
    action: PropTypes.oneOf(["close"]),
  };
}

export class ModalSaveButton extends SaveButton {
  static propTypes = {
    action: PropTypes.oneOf(["close"]),
  };
}

export default Modal;
